import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControlLabel,
    FormGroup,
    FormLabel,
    OutlinedInput,
    Radio,
    RadioGroup,
    Typography,
    styled
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';
import AdminSettingsContainer from '../AdminSettingsContainer';
import { UserAccountCreationOptions } from '../../../types/adminConfig';
import { StyledFormControl } from '../styles';
import { AdminControlCheckbox } from '../utils/AdminControlCheckbox';

const StyledAccordian = styled(Accordion)`
    margin-left: 10px;
    margin-bottom: 28px;
    width: 40rem;
`;

const StyledFormGroup = styled(FormGroup)`
    margin-left: 15px;
    margin-bottom: 16px;
`;

const StyledInput = styled(OutlinedInput)`
    margin-left: 3rem;
    margin-right: 1rem;
    margin-bottom: 1rem;
    width: 32rem;
`;

const UserAccountCreationSetting = () => {
    const { adminSettings, setAdminSettings, setSavingDisabled } = useContainer(AdminSettingsContainer);
    const {
        featureSettings: { options }
    } = adminSettings;
    const { t } = useTranslation('admin_settings');

    const [disableDomainNames, setDisableDomainNames] = useState(
        options.userAccountCreation !== UserAccountCreationOptions.Domains
    );

    const disableSaveButton = () => {
        setSavingDisabled(
            options.userAccountCreation === UserAccountCreationOptions.Domains &&
                (!options.userAccountCreationDomains ||
                    options.userAccountCreationDomains.length === 0 ||
                    options.userAccountCreationDomains.some((d) => !d || d.includes(' ')))
        );
    };

    const handleDomainsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Make sure spaces are trimmed from the domains
        options.userAccountCreationDomains = event.target.value.split(',').map((d) => d?.trim());
        setAdminSettings({ ...adminSettings });
        disableSaveButton();
    };

    const handleUpdateOptionsRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        options[event.target.name] = event.target.value;
        setAdminSettings({ ...adminSettings });
        setDisableDomainNames(options.userAccountCreation !== UserAccountCreationOptions.Domains);
        disableSaveButton();
    };

    return (
        <>
            <StyledAccordian>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                    <Typography>{t('auth.account_create-and-sign-on')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <StyledFormGroup>
                        <FormLabel id="user-account-creation-label">{t('auth.user-account-creation.title')}</FormLabel>
                        <RadioGroup
                            aria-labelledby="user-account-creation-label"
                            name="userAccountCreation"
                            value={
                                options.userAccountCreation ??
                                (adminSettings.zoomAppId !== 'teams'
                                    ? UserAccountCreationOptions.AdminOnly
                                    : UserAccountCreationOptions.AllUsers)
                            }
                            onChange={handleUpdateOptionsRadio}
                        >
                            <StyledFormControl>
                                <FormControlLabel
                                    value={UserAccountCreationOptions.AdminOnly}
                                    control={<Radio />}
                                    label={t('auth.user-account-creation.admin')}
                                />
                            </StyledFormControl>
                            <FormControlLabel
                                value={UserAccountCreationOptions.Domains}
                                control=<Radio />
                                label={t('auth.user-account-creation.domain')}
                            />
                            <AdminControlCheckbox
                                control={
                                    <StyledInput
                                        value={options.userAccountCreationDomains?.join(', ').trim() ?? ''}
                                        onChange={handleDomainsChange}
                                        id="domains"
                                        name="domains"
                                        placeholder={t('auth.user-account-creation.domains-placeholder')}
                                        disabled={disableDomainNames}
                                    />
                                }
                                label=""
                            />
                            <FormControlLabel
                                value={UserAccountCreationOptions.AllUsers}
                                control=<Radio />
                                label={t('auth.user-account-creation.anyone')}
                            />
                        </RadioGroup>
                    </StyledFormGroup>
                </AccordionDetails>
            </StyledAccordian>
        </>
    );
};

export default UserAccountCreationSetting;
